import {
  PDF_DEFAULT_FONT,
  PDF_HEADING_FONT_SIZES,
  pdfFontWeightLabel,
  pdfFontWeights,
} from "@repo/shared/types";
import { HexColorPicker } from "react-colorful";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./pdf-template-heading-style.i18n";

/** Radix Select cannot hold "" as a value; this stands for "not set". */
const DEFAULT_VALUE = "default";

export type HeadingStyleValue = {
  /** "" = default size. */
  headingFontSize: string;
  /** "" = default weight. */
  headingFontWeight: string;
  /** "" = default colour. */
  headingColor: string;
  headingNumbered: boolean;
};

type PdfTemplateHeadingStyleProps = {
  value: HeadingStyleValue;
  onChange: (value: HeadingStyleValue) => void;
  /** The font chosen in Typography; "" = the default font. */
  fontFamily: string;
  /** Set when a font change moved the weight; shown under the weight select. */
  weightAdjustment: { from: number; to: number } | null;
  disabled?: boolean;
};

export function PdfTemplateHeadingStyle({
  value,
  onChange,
  fontFamily,
  weightAdjustment,
  disabled,
}: PdfTemplateHeadingStyleProps) {
  const t = useT(T);
  const font = fontFamily || PDF_DEFAULT_FONT;
  const weights = pdfFontWeights(font);

  return (
    <div className="border rounded-lg p-4 space-y-4">
      <h3 className="font-semibold">{t.headings}</h3>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="headingFontSize">{t.fontSize}</Label>
          <Select
            value={value.headingFontSize || DEFAULT_VALUE}
            onValueChange={(size) =>
              onChange({ ...value, headingFontSize: size === DEFAULT_VALUE ? "" : size })
            }
            disabled={disabled}
          >
            <SelectTrigger id="headingFontSize">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={DEFAULT_VALUE}>{t.default}</SelectItem>
              {PDF_HEADING_FONT_SIZES.map((size) => (
                <SelectItem key={size} value={size.toString()}>
                  {size}pt
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="headingFontWeight">{t.weight}</Label>
          <Select
            value={value.headingFontWeight || DEFAULT_VALUE}
            onValueChange={(weight) =>
              onChange({ ...value, headingFontWeight: weight === DEFAULT_VALUE ? "" : weight })
            }
            disabled={disabled}
          >
            <SelectTrigger id="headingFontWeight">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={DEFAULT_VALUE}>{t.default}</SelectItem>
              {weights.map((weight) => (
                <SelectItem key={weight} value={weight.toString()}>
                  {pdfFontWeightLabel(weight)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            {weightAdjustment
              ? t.weightAdjusted({
                  font: font,
                  from: pdfFontWeightLabel(weightAdjustment.from),
                  to: pdfFontWeightLabel(weightAdjustment.to),
                })
              : t.weightHelp(font)}
          </p>
        </div>
      </div>

      <div className="space-y-2">
        <Label>{t.colour}</Label>
        <div className="flex items-center gap-3">
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="w-10 h-10 rounded-lg border-2 border-input cursor-pointer hover:ring-2 hover:ring-ring hover:ring-offset-2 transition-all shadow-sm"
                style={{ backgroundColor: value.headingColor || "#000000" }}
                disabled={disabled}
              />
            </PopoverTrigger>
            <PopoverContent className="w-auto p-3" align="start">
              <HexColorPicker
                color={value.headingColor || "#000000"}
                onChange={(color) => onChange({ ...value, headingColor: color })}
              />
              <Input
                type="text"
                value={value.headingColor}
                onChange={(e) => onChange({ ...value, headingColor: e.target.value })}
                placeholder="#000000"
                className="mt-2 w-full font-mono text-sm"
                disabled={disabled}
              />
            </PopoverContent>
          </Popover>
          <span className="font-mono text-sm text-muted-foreground">
            {value.headingColor || t.defaultColour}
          </span>
          {value.headingColor && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onChange({ ...value, headingColor: "" })}
              disabled={disabled}
            >
              {t.clear}
            </Button>
          )}
        </div>
      </div>

      <div className="space-y-1">
        <div className="flex items-center space-x-2">
          <Checkbox
            id="headingNumbered"
            checked={value.headingNumbered}
            onCheckedChange={(checked) => onChange({ ...value, headingNumbered: checked === true })}
            disabled={disabled}
          />
          <Label htmlFor="headingNumbered" className="font-normal cursor-pointer">
            {t.numberHeadings}
          </Label>
        </div>
        <p className="text-xs text-muted-foreground pl-6">{t.numberHeadingsHelp}</p>
      </div>
    </div>
  );
}
